import React, { useState, useRef, createRef, useLayoutEffect, forwardRef, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { HorizontalTabItem, HorizontalTabHighlight } from '../../atoms';
import { usePrevious } from '../../../hooks';

const HorizontalTabsWrapper = styled.nav.attrs(() => ({ role: 'tablist' }))`
  display: flex;
  flex-shrink: 0;
  padding: 0 40px;
  position: relative;
  overflow-x: scroll;
  column-gap: 28px;
`;

const HorizontalTabs = forwardRef(({ tabs, initialActiveTabIndex = 0, className = null }, ref) => {
  const tabRefs = useRef(tabs.map(() => createRef()));
  const [activeTabIndex, setActiveTabIndex] = useState(initialActiveTabIndex);
  const [activeTab, setActiveTab] = useState(null);
  const previousActiveTab = usePrevious(activeTab);

  const handleTabClick = (tab, index) => {
    tab.onClick?.call(index);
    setActiveTabIndex(index);
    setActiveTab(tabRefs.current[index].current);
  };

  useEffect(() => {
    setActiveTabIndex(initialActiveTabIndex);
    setActiveTab(tabRefs.current[initialActiveTabIndex].current);
  }, [initialActiveTabIndex]);

  useLayoutEffect(() => {
    setActiveTab(tabRefs.current[activeTabIndex]?.current);
  }, []);

  return (
    <HorizontalTabsWrapper ref={ref} className={className}>
      {tabs.map((tab, index) => (
        <HorizontalTabItem
          onClick={() => handleTabClick(tab, index)}
          active={activeTabIndex === index}
          ref={tabRefs.current[index]}
          key={tab.title}
        >
          {tab.component}
        </HorizontalTabItem>
      ))}
      <HorizontalTabHighlight
        disableTransition={!previousActiveTab}
        style={{ left: activeTab?.offsetLeft, width: activeTab?.clientWidth }}
      />
    </HorizontalTabsWrapper>
  );
});

HorizontalTabs.propTypes = {
  initialActiveTabIndex: PropTypes.number,
  className: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
      title: PropTypes.string
    })
  ).isRequired
};

export { HorizontalTabs };
